import { useState } from "react";
import { LocateFixed, Loader2, Search, X } from "lucide-react";
import postcodes from "@/lib/auPostcodes.json";

/**
 * NearMeSearch — lets a family find their nearest provider by entering an
 * Australian postcode or sharing their device location. Resolves to a
 * { lat, lng, label } origin passed up via onLocate.
 */
export default function NearMeSearch({ onLocate, onClear, active }) {
  const [pc, setPc] = useState("");
  const [error, setError] = useState("");
  const [locating, setLocating] = useState(false);

  const searchPostcode = (e) => {
    e.preventDefault();
    setError("");
    const code = pc.trim();
    if (!/^\d{4}$/.test(code)) {
      setError("Please enter a 4 digit Australian postcode.");
      return;
    }
    const hit = postcodes[code];
    if (!hit) {
      setError("We could not find that postcode. Please try another.");
      return;
    }
    onLocate({ lat: hit[0], lng: hit[1], label: `Postcode ${code}` });
  };

  const useMyLocation = () => {
    setError("");
    if (!navigator.geolocation) {
      setError("Location is not available in this browser. Try a postcode.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onLocate({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: "your location",
        });
      },
      () => {
        setLocating(false);
        setError("We could not access your location. Please try a postcode instead.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  return (
    <div
      className="card-elevated rounded-3xl p-6 sm:p-8"
      data-testid="near-me-search"
    >
      <div className="overline mb-2">Find A Provider Near You</div>
      <h3 className="font-serif text-2xl sm:text-3xl text-forest-900 leading-tight tracking-tight mb-5">
        See which provider is closest to you
      </h3>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <form onSubmit={searchPostcode} className="flex gap-2 flex-1">
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={pc}
            onChange={(e) => setPc(e.target.value.replace(/\D/g, ""))}
            placeholder="Enter postcode (e.g. 4810)"
            aria-label="Australian postcode"
            data-testid="near-me-postcode-input"
            className="flex-1 min-h-[48px] rounded-full border border-sand-300 bg-white px-5 text-forest-900 placeholder:text-forest-400 focus:outline-none focus:border-forest-900/40"
          />
          <button
            type="submit"
            className="btn-primary !px-5 min-h-[48px]"
            data-testid="near-me-search-button"
            aria-label="Search by postcode"
          >
            <Search size={18} />
          </button>
        </form>

        <span className="text-sm text-forest-500 text-center sm:px-1">or</span>

        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="btn-secondary min-h-[48px] justify-center"
          data-testid="near-me-geolocate-button"
        >
          {locating ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <LocateFixed size={16} />
          )}
          {locating ? "Locating" : "Use my location"}
        </button>
      </div>

      {error && (
        <p className="text-sm text-clay-600 mt-3" data-testid="near-me-error">
          {error}
        </p>
      )}

      {active && (
        <div
          className="mt-4 flex items-center justify-between gap-4 rounded-2xl bg-sage-50 border border-sage-100 px-4 py-3"
          data-testid="near-me-active"
        >
          <p className="text-sm text-forest-700">
            Showing providers nearest to{" "}
            <span className="font-medium text-forest-900">{active.label}</span>.
          </p>
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-forest-600 hover:text-forest-900 inline-flex items-center gap-1 shrink-0"
            data-testid="near-me-clear"
          >
            <X size={14} /> Clear
          </button>
        </div>
      )}
    </div>
  );
}
